// Простой разбор Markdown ответов ИИ: заголовки, списки, абзацы, **жирный**, *курсив*, `код`.
// Без HTML: текст рисуется как текст, разметка модели не может вставить свой код.

export type Inline = { text: string; bold?: true; italic?: true; code?: true }

export type Block =
  | { type: 'heading'; level: number; content: Inline[] }
  | { type: 'paragraph'; lines: Inline[][] }
  | { type: 'list'; ordered: boolean; items: Inline[][] }

const INLINE = /\*\*(.+?)\*\*|`([^`]+)`|\*(?=\S)(.+?)(?<=\S)\*/g

export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let last = 0
  for (const match of text.matchAll(INLINE)) {
    const index = match.index ?? 0
    if (index > last) out.push({ text: text.slice(last, index) })
    if (match[1] !== undefined) out.push({ text: match[1], bold: true })
    else if (match[2] !== undefined) out.push({ text: match[2], code: true })
    else if (match[3] !== undefined) out.push({ text: match[3], italic: true })
    last = index + match[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last) })
  return out
}

const HEADING = /^(#{1,6})\s+(.*)$/
const BULLET = /^\s*[-*•]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/

export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = []
  let paragraph: Inline[][] | null = null
  let list: { ordered: boolean; items: Inline[][] } | null = null

  const flush = () => {
    if (paragraph) blocks.push({ type: 'paragraph', lines: paragraph })
    if (list) blocks.push({ type: 'list', ...list })
    paragraph = null
    list = null
  }

  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd()
    if (!line.trim() || /^\s*(-{3,}|\*{3,})\s*$/.test(line)) {
      flush()
      continue
    }
    const heading = HEADING.exec(line)
    if (heading) {
      flush()
      blocks.push({ type: 'heading', level: heading[1]!.length, content: parseInline(heading[2]!.trim()) })
      continue
    }
    const bullet = BULLET.exec(line)
    const numbered = bullet ? null : NUMBERED.exec(line)
    const item = bullet ?? numbered
    if (item) {
      const ordered = numbered !== null
      if (paragraph || (list && list.ordered !== ordered)) flush()
      list ??= { ordered, items: [] }
      list.items.push(parseInline(item[1]!.trim()))
      continue
    }
    if (list) flush()
    paragraph ??= []
    paragraph.push(parseInline(line.trim()))
  }
  flush()
  return blocks
}
